export type JsonRecord = Record<string, unknown>

export type DropshipDraftLike = {
  draft_fields?: unknown
  review_payload?: unknown
  validation_errors?: string[] | null
  validation_summary?: string | null
  manual_review_flags?: unknown
}

export type DraftTotals = {
  subtotal: string | null
  shipping: unknown
  tax: unknown
  total: unknown
  usesComputedOrderTotal: boolean
}

export function asRecord(value: unknown): JsonRecord {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as JsonRecord) : {}
}

export function asRecords(value: unknown): JsonRecord[] {
  return Array.isArray(value)
    ? value.filter(
        (item): item is JsonRecord => !!item && typeof item === 'object' && !Array.isArray(item),
      )
    : []
}

export function friendlyValue(value: unknown): string | null {
  if (value == null || value === '') return null
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  return null
}

export function unwrapFieldPayload(value: unknown): unknown {
  const record = asRecord(value)
  return Object.prototype.hasOwnProperty.call(record, 'value') ? record.value : value
}

export function nestedValue(record: JsonRecord, key: string): unknown {
  return key.split('.').reduce<unknown>((current, part) => asRecord(current)[part], record)
}

export function fieldValue(draft: DropshipDraftLike, key: string): unknown {
  const fields = asRecord(draft.draft_fields)
  if (fields[key] !== undefined) return unwrapFieldPayload(fields[key])
  const payload = asRecord(draft.review_payload)
  const nested = nestedValue(payload, key)
  if (nested !== undefined) return unwrapFieldPayload(nested)
  if (key === 'ship_to.contact') {
    const shipTo = asRecord(payload.ship_to)
    return unwrapFieldPayload(shipTo.contact ?? shipTo.contact_name ?? shipTo.name)
  }
  return undefined
}

export function quoteLineItems(draft: DropshipDraftLike): JsonRecord[] {
  const fields = asRecord(draft.draft_fields)
  const payload = asRecord(draft.review_payload)
  const quote = asRecord(payload.quote)
  const financials = asRecord(payload.financials)
  const patchedQuoteLines = asRecords(fields['quote.line_items'])
  const quoteLines = patchedQuoteLines.length ? patchedQuoteLines : asRecords(quote.line_items)
  const financialLines = asRecords(financials.line_items)
  const lineCount = Math.max(quoteLines.length, financialLines.length)

  return Array.from({ length: lineCount }, (_, index) => ({
    ...financialLines[index],
    ...quoteLines[index],
  }))
}

export function reviewLines(draft: DropshipDraftLike): JsonRecord[] {
  return asRecords(asRecord(asRecord(draft.review_payload).financials).line_items)
}

export function lineFieldValue(line: JsonRecord, reviewLine: JsonRecord, field: string): unknown {
  if (field === 'unit_price') return line.list_price ?? reviewLine.list_price ?? line.unit_price
  if (field === 'sell_price') return line.sell_price ?? reviewLine.sell_price ?? line.unit_price
  if (field === 'net_price') return line.net_price ?? line.cost_price ?? reviewLine.net_price
  return line[field] ?? reviewLine[field]
}

export function numberValue(value: unknown): number | null {
  if (value == null || value === '') return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

export function lineSellTotal(line: JsonRecord, reviewLine: JsonRecord): string | null {
  const explicit = line.sell_total ?? line.line_total ?? reviewLine.sell_total
  if (explicit != null && explicit !== '') return String(explicit)
  const qty = numberValue(lineFieldValue(line, reviewLine, 'quantity'))
  const sell = numberValue(lineFieldValue(line, reviewLine, 'sell_price'))
  if (qty == null || sell == null) return null
  return (qty * sell).toFixed(2)
}

export function computedSubtotal(draft: DropshipDraftLike): string | null {
  const reviewTotal = asRecord(
    asRecord(asRecord(draft.review_payload).financials).totals,
  ).line_sell_total
  if (reviewTotal != null && reviewTotal !== '') return String(reviewTotal)
  const lines = quoteLineItems(draft)
  const computedLines = reviewLines(draft)
  const sum = lines.reduce((total, line, index) => {
    const sellTotal = numberValue(lineSellTotal(line, computedLines[index] ?? {}))
    return total + (sellTotal ?? 0)
  }, 0)
  return lines.length ? sum.toFixed(2) : null
}

export function draftTotals(draft: DropshipDraftLike): DraftTotals {
  const financials = asRecord(asRecord(draft.review_payload).financials)
  const totalRecord = asRecord(financials.totals)
  const subtotal = computedSubtotal(draft)
  const shipping = totalRecord.freight_total ?? fieldValue(draft, 'quote.freight_total')
  const tax = totalRecord.tax_total ?? fieldValue(draft, 'quote.tax_total')
  const computedOrderTotal = totalRecord.computed_order_total
  const total =
    computedOrderTotal != null && computedOrderTotal !== ''
      ? computedOrderTotal
      : ((numberValue(subtotal) ?? 0) + (numberValue(shipping) ?? 0) + (numberValue(tax) ?? 0)).toFixed(2)
  return {
    subtotal,
    shipping,
    tax,
    total,
    usesComputedOrderTotal: computedOrderTotal != null && computedOrderTotal !== '',
  }
}

function humanizeKey(key: string): string {
  return key
    .split('.')
    .map((part) => part.replace(/_/g, ' '))
    .join(' › ')
}

function issueDetailsText(details: JsonRecord): string | null {
  const parts = [
    ['stored', details.stored_value],
    ['current', details.current_value],
    ['delta', details.delta],
    ['parsed', details.parsed_quote_total],
    ['computed', details.computed_order_total],
  ]
    .map(([label, value]) => {
      const rendered = friendlyValue(value)
      return rendered ? `${label} ${rendered}` : null
    })
    .filter((part): part is string => !!part)
  return parts.length ? parts.join(', ') : null
}

function issueMessage(issue: JsonRecord): string | null {
  const message = friendlyValue(issue.message) ?? friendlyValue(issue.reason) ?? friendlyValue(issue.detail)
  const field = friendlyValue(issue.field)
  const code = friendlyValue(issue.code)
  const details = issueDetailsText(asRecord(issue.details))
  const prefix = field ? humanizeKey(field) : code ? humanizeKey(code) : null
  const body = [message, details ? `(${details})` : null]
    .filter((part): part is string => !!part)
    .join(' ')
  if (prefix && body) return `${prefix}: ${body}`
  return body || prefix
}

function addFlagDetails(details: Set<string>, flags: JsonRecord): void {
  for (const [key, rawValue] of Object.entries(flags)) {
    if (!rawValue) continue
    const reason = rawValue === true ? null : friendlyValue(unwrapFieldPayload(rawValue))
    details.add(reason ? `${humanizeKey(key)}: ${reason}` : `${humanizeKey(key)} needs review`)
  }
}

function addStructuredValidationDetails(details: Set<string>, value: unknown): void {
  const record = asRecord(value)
  for (const issue of asRecords(record.issues ?? record.validation_issues)) {
    const rendered = issueMessage(issue)
    if (rendered) details.add(rendered)
  }
  const errors = Array.isArray(record.validation_errors) ? record.validation_errors : []
  for (const error of errors) {
    const rendered = friendlyValue(error)
    if (rendered) details.add(rendered)
  }
  addFlagDetails(
    details,
    asRecord(record.unresolved_manual_review_flags ?? record.manual_review_flags),
  )
}

export function draftValidationDetails(draft: DropshipDraftLike): string[] {
  const details = new Set<string>()
  const summary = friendlyValue(draft.validation_summary)
  if (summary) details.add(summary)
  addStructuredValidationDetails(details, asRecord(draft.review_payload).validation_issues ? { issues: asRecord(draft.review_payload).validation_issues } : {})
  for (const error of draft.validation_errors ?? []) {
    const trimmed = error.trim()
    if (trimmed) details.add(trimmed)
  }
  addFlagDetails(details, asRecord(draft.manual_review_flags))
  return [...details]
}

export function getDropshipErrorMessage(error: unknown, fallback: string, draft?: DropshipDraftLike): string {
  const detail = asRecord(error).detail
  const detailRecord = asRecord(detail)
  const base =
    error instanceof Error && error.message
      ? error.message
      : typeof detail === 'string' && detail.trim()
        ? detail
        : typeof detailRecord.detail === 'string' && detailRecord.detail.trim()
          ? detailRecord.detail
        : fallback
  const combinedDetails = new Set<string>()
  addStructuredValidationDetails(combinedDetails, detailRecord)
  addStructuredValidationDetails(combinedDetails, error)
  if (draft) {
    for (const detailMessage of draftValidationDetails(draft)) combinedDetails.add(detailMessage)
  }
  const validationDetails = [...combinedDetails]
  if (!validationDetails.length) return base
  return `${base}: ${validationDetails.join('; ')}`
}
